<template>
  <section
    :class="[
      'relative flex overflow-hidden -mt-[calc(5rem+var(--safe-top))] lg:-mt-32 pt-[calc(5rem+var(--safe-top))] lg:pt-32 bg-degradado-bochas',
      sectionClass,
    ]"
  >
    <!-- Imagen configurada desde administración o degradado institucional -->
    <!-- Si parallax está activo, la imagen se mueve con el scroll a velocidad reducida -->
    <img
      v-if="imageUrl"
      :src="imageUrl"
      :alt="alt"
      :style="
        parallax ? { transform: `translate3d(0, ${parallaxOffset}px, 0)` } : {}
      "
      class="absolute inset-0 w-full h-full object-cover opacity-50 will-change-transform"
    />
    <div v-else class="absolute inset-0 bg-degradado-bochas"></div>

    <!-- Gradient overlay -->
    <div
      class="absolute inset-0 bg-gradient-to-t from-bochas-bg via-bochas-bg/40 to-transparent"
    ></div>

    <!-- Contenido (con fade-in al montar) -->
    <div :class="contentClass" data-hero-content :data-hero-mounted="isMounted">
      <!-- Slot top: bloque icon+label (opcional, antes del title) -->
      <slot name="top">
        <div
          v-if="topIcon || topLabel"
          :class="[
            'flex items-center gap-3 mb-3',
            align === 'center' ? 'justify-center' : '',
          ]"
        >
          <IconFbochas v-if="topIcon" :name="topIcon" :size="40" />
          <span
            v-if="topLabel"
            class="text-xs sm:text-sm font-bold uppercase tracking-wider text-white"
            >{{ topLabel }}</span
          >
        </div>
      </slot>

      <!-- Title -->
      <h1 v-if="title" :class="titleClass">
        {{ title }}
      </h1>

      <!-- Subtitle -->
      <p v-if="subtitle" :class="subtitleClass">
        {{ subtitle }}
      </p>

      <!-- Slot default: contenido extra (CTAs, etc.) -->
      <slot />
    </div>
  </section>
</template>

<script setup>
import { ref, computed, onMounted } from "vue";
import IconFbochas from "@/components/Icons/IconFbochas.vue";
import { useScrollY } from "@/composables/useScrollY";

// Fade-in del contenido del hero cuándo se monta
const isMounted = ref(false);
onMounted(() => {
  // Doble rAF para asegurar que el browser pinte el estado inicial antes de la transición
  requestAnimationFrame(() => {
    requestAnimationFrame(() => {
      isMounted.value = true;
    });
  });
});

const props = defineProps({
  // Si no hay una imagen configurada, se conserva el degradado institucional.
  imageUrl: { type: String, default: "" },
  loadingImage: { type: Boolean, default: false },
  seed: { type: String, default: "" },
  category: { type: String, default: "bochas" },
  width: { type: Number, default: 1920 },
  height: { type: Number, default: 900 },
  alt: { type: String, default: "Imagen de fondo" },

  // Contenido
  title: { type: String, default: "" },
  subtitle: { type: String, default: "" },
  topIcon: { type: String, default: "" },
  topLabel: { type: String, default: "" },

  // Variantes de layout
  // - "default" + "left": 70vh en lg, py-24 pb-12 (Federación, Calendario, etc.)
  // - "compact" + "left": 60vh en lg, py-12 (Historias, Pasado, Alojamiento)
  // - "large"   + "center": 100vh en lg, centrado (Jugadores, Jóvenes)
  size: {
    type: String,
    default: "default",
    validator: (v) => ["default", "compact", "large"].includes(v),
  },
  align: {
    type: String,
    default: "left",
    validator: (v) => ["left", "center"].includes(v),
  },

  // Parallax: si está activo, la imagen de fondo se mueve con el scroll
  // a velocidad reducida (efecto de profundidad). Default true.
  // Factor típico: 0.3-0.5 (menor = la imagen se mueve menos).
  // El `bg-degradado-bochas` del section queda visible en los huecos
  // que deja la imagen al moverse, así que no se notan los bordes.
  parallax: { type: Boolean, default: true },
  parallaxFactor: { type: Number, default: 0.4 },
});

// Parallax: calculamos el offset en función del scroll y el factor
const scrollY = useScrollY();
const parallaxOffset = computed(() => {
  if (!props.parallax) return 0;
  return scrollY.value * props.parallaxFactor;
});

const sectionClass = computed(() => {
  if (props.size === "compact") {
    return props.align === "center"
      ? "min-h-[50vh] lg:min-h-[60vh] items-center justify-center text-center px-6 py-12"
      : "min-h-[50vh] lg:min-h-[60vh] items-center px-6 lg:px-16 py-12";
  }
  if (props.size === "large") {
    return "min-h-[50vh] lg:min-h-[100vh] 2xl:min-h-[80vh] items-center justify-center text-center px-6 py-24";
  }
  // default
  return "min-h-[50vh] lg:min-h-[70vh] 2xl:min-h-[50vh] items-center px-6 sm:px-10 lg:px-16 py-24 pb-12";
});

const contentClass = computed(() => {
  if (props.align === "center") {
    return "relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full";
  }
  // left
  if (props.size === "compact") {
    return "relative max-w-7xl mx-auto w-full";
  }
  return "relative w-full sm:mt-auto max-w-7xl text-left";
});

const titleClass = computed(() => {
  if (props.size === "compact") {
    return "text-4xl sm:text-5xl lg:text-6xl font-extrabold text-white mb-4 leading-tight";
  }
  if (props.size === "large" || props.align === "center") {
    return "text-3xl sm:text-4xl lg:text-5xl font-extrabold text-white mb-3";
  }
  return "text-3xl sm:text-4xl md:text-4xl lg:text-[40px] font-extrabold text-white mb-4 leading-[1.05]";
});

const subtitleClass = computed(() => {
  if (props.size === "compact") {
    return "text-lg sm:text-xl md:text-2xl text-white font-medium max-w-3xl";
  }
  if (props.size === "large" || props.align === "center") {
    return "text-base sm:text-lg text-gray-200";
  }
  return "font-semibold text-white text-2xl sm:text-lg md:text-3xl max-w-7xl";
});
</script>

<style>
/* ===== FADE-IN DEL CONTENIDO DEL HERO AL MONTAR ===== */
[data-hero-content] {
  opacity: 0;
  transform: translateY(16px);
  transition: opacity 0.8s ease-out,
    transform 0.8s cubic-bezier(0.22, 1, 0.36, 1);
}
[data-hero-content][data-hero-mounted="true"] {
  opacity: 1;
  transform: translateY(0);
}

@media (prefers-reduced-motion: reduce) {
  [data-hero-content] {
    opacity: 1;
    transform: none;
    transition: none;
  }
}
</style>
